import { useState, useRef } from 'react';
import { X, User, Mail, Phone, GraduationCap, Globe, Music, Heart, Plus, Loader2, Camera, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UserProfile, UpdateProfilePayload } from '@/types';
import { profileApi } from '@/lib/api';
import { toast } from 'sonner';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  onSave: (data: UpdateProfilePayload) => Promise<void>;
}

export const ProfileEditModal = ({ isOpen, onClose, user, onSave }: ProfileEditModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar || null);
  const [formData, setFormData] = useState<UpdateProfilePayload>({
    name: user?.name || '',
    phone_number: user?.phone_number || '',
    major: user?.major || '',
    languages: user?.languages || ['English'],
    favoriteMusic: user?.favoriteMusic || '',
    hobbies: user?.hobbies || [],
    avatar: user?.avatar || '',
  });
  
  const [newHobby, setNewHobby] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    if (formData.phone_number && !/^\d{10,15}$/.test(formData.phone_number.replace(/\D/g, ''))) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      toast.success('Profile updated successfully!');
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddHobby = () => {
    if (newHobby.trim() && !formData.hobbies?.includes(newHobby.trim())) {
      setFormData(prev => ({
        ...prev,
        hobbies: [...(prev.hobbies || []), newHobby.trim()]
      }));
      setNewHobby('');
    }
  };

  const handleRemoveHobby = (hobby: string) => {
    setFormData(prev => ({
      ...prev,
      hobbies: prev.hobbies?.filter(h => h !== hobby) || []
    }));
  };

  const handleAddLanguage = () => {
    if (newLanguage.trim() && !formData.languages?.includes(newLanguage.trim())) {
      setFormData(prev => ({
        ...prev,
        languages: [...(prev.languages || []), newLanguage.trim()]
      }));
      setNewLanguage('');
    }
  };

  const handleRemoveLanguage = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages?.filter(l => l !== language) || []
    }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to backend
    setIsUploading(true);
    try {
      const avatarUrl = await profileApi.uploadAvatar(file);
      setFormData(prev => ({ ...prev, avatar: avatarUrl }));
      toast.success('Avatar uploaded successfully!');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload avatar');
      // Reset preview on error
      setAvatarPreview(user?.avatar || null);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">Edit Profile</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Avatar Upload */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative">
              <div 
                className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center cursor-pointer group"
                onClick={handleAvatarClick}
              >
                {avatarPreview ? (
                  <img 
                    src={avatarPreview} 
                    alt="Avatar preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-muted-foreground" />
                )}
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>
              {/* Upload icon button */}
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={isUploading}
                className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-colors"
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Click to upload a profile picture
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Your name"
              required
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
              placeholder="1234567890"
            />
          </div>

          {/* Major */}
          <div className="space-y-2">
            <Label htmlFor="major" className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Major
            </Label>
            <Input
              id="major"
              value={formData.major}
              onChange={(e) => setFormData(prev => ({ ...prev, major: e.target.value }))}
              placeholder="Computer Science"
            />
          </div>

          {/* Favorite Music */}
          <div className="space-y-2">
            <Label htmlFor="music" className="flex items-center gap-2">
              <Music className="w-4 h-4" />
              Favorite Music
            </Label>
            <Input
              id="music"
              value={formData.favoriteMusic}
              onChange={(e) => setFormData(prev => ({ ...prev, favoriteMusic: e.target.value }))}
              placeholder="Rock, Pop, Jazz..."
            />
          </div>

          {/* Languages */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Languages
            </Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(formData.languages || []).map((lang, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-1"
                >
                  {lang}
                  <button
                    type="button"
                    onClick={() => handleRemoveLanguage(lang)}
                    className="hover:text-destructive"
                    disabled={isSubmitting}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                placeholder="Add language"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddLanguage}
                disabled={!newLanguage.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Hobbies */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              Hobbies
            </Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(formData.hobbies || []).map((hobby, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-accent/10 text-accent rounded-full text-sm flex items-center gap-1"
                >
                  {hobby}
                  <button
                    type="button"
                    onClick={() => handleRemoveHobby(hobby)}
                    className="hover:text-destructive"
                    disabled={isSubmitting}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newHobby}
                onChange={(e) => setNewHobby(e.target.value)}
                placeholder="Add hobby"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHobby())}
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddHobby}
                disabled={!newHobby.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfileEditModal;

